<template>
    <div
        :id="id"
        ref="container"
        class="tl-input-file-container"
    >
        <div
            v-for="(document, index) in Object.values(modelState || {})"
            :key="index"
            class="tl-input-file-document"
            :class="{
                'tl-input-file-document-disabled': isDisabled || document.disabled || document.loading,
                'tl-input-file-document-invalid': !!computedError
            }"
        >
            <InputFileUpload
                :id="props.id"
                v-model="document.files"
                :extensions="parsedExtensions"
                :loading="document.loading"
                :progress="document.progress"
                :disabled="isDisabled || document.disabled"
                :max-file-size="maxFileSize"
                :label="label"
                :upload-limit="uploadLimit"
                @error="handleDocumentError(document, $event)"
                @file:add="emit('file:add', $event)"
                @file:remove="emit('file:remove', $event)"
                @download-document="emit('download-document', $event)"
            />
        </div>

        <ErrorText
            v-if="computedError"
            class="general-error"
            :error="computedError"
        />
    </div>
</template>

<script setup lang="ts">
import {
    ref, watch, computed, onMounted, inject, nextTick, type ComputedRef,
} from 'vue'
import { useI18n } from 'widget-components/utilities/i18n'
import InputFileUpload from 'widget-components/components/InputFileUpload/InputFileUpload.vue'
import ErrorText from 'widget-components/components/ErrorText/ErrorText.vue'
import equals from 'fast-deep-equal/es6'
import type { InputDocumentItem, InputDocumentModel } from 'widget-components/components/InputFileUpload/types'
import type { BlockPath, Resource } from 'widget-components/integrations/CustomPage/types'
import { isEmpty } from 'ramda'
import { autoFocusOnMounted } from 'widget-components/integrations/CustomPage/autofocus'

const customPageBuilder = inject<boolean>('customPageBuilder', false)
const customPagePreview = inject<ComputedRef<boolean>>('customPagePreview', computed(() => false))

const { t } = useI18n()

defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<{
    id?: string
    modelValue: Record<string, InputDocumentModel>
    initialValue?: Record<string, InputDocumentModel>
    disabled?: boolean
    maxFileSize?: number
    extensions: string[]
    error?: string | boolean
    label?: string
    uploadLimit?: string | number
    blockPath?: BlockPath
    defaultResources?: Resource[]
    focus?: boolean
}>(), {
    modelValue: () => ({}),
    maxFileSize: 10 * 1024 * 1024, // 10MB,
    extensions: () => [],
})

const isDisabled = computed(() => props.disabled || customPagePreview.value)

const parsedExtensions = computed(() => (props.extensions).join(','))

const documentsError = computed(
    () => Object
        .values(modelState.value)
        .find(document => document.error?.length)?.error || '',
)

const computedError = computed(() => {
    if (props.error) {
        return props.error
    }
    return documentsError.value
})

const handleDocumentError = (document: InputDocumentItem, error?: string) => {
    document.error = error
}

const emit = defineEmits([
    'update:modelValue',
    'error',
    'file:add',
    'file:remove',
    'download-document',
])

const INITIAL_MODEL_STATE = {
    document: {
        inputFileId: 'unused',
        files: {},
    } as InputDocumentModel,
}

const modelState = ref(props.modelValue)
const container = ref<HTMLElement>()

autoFocusOnMounted(
    props,
    () => container.value?.querySelector<HTMLElement>('button.tl-input-drop-zone'),
    { beforeFocus: nextTick },
)

onMounted(() => {
    if (props.initialValue) {
        modelState.value = props.initialValue
    } else if (isEmpty(modelState.value)) {
        modelState.value = { ...INITIAL_MODEL_STATE }
    }

    watch(() => computedError.value, (err) => {
        emit('error', err || '')
    }, { immediate: true })
})

if (!customPageBuilder) {
    watch(modelState, (val) => {
        const filesCount = Object.keys(val.document.files).length
        const maxFilesCount = Number(props.uploadLimit)

        if (filesCount > maxFilesCount) {
            modelState.value.document.error = t('input-file-upload.error.upload-limit', { maxFilesCount })
        } else {
            modelState.value.document.error = undefined
        }

        emit('update:modelValue', val)
    }, { deep: true })

    watch(() => props.modelValue, (val) => {
        if (!equals(val, modelState.value)) {
            modelState.value = val
        }
    }, { deep: true })
}
</script>

<style scoped lang="sass">
@import './InputFile.sass'
</style>
